import { useEffect, useMemo, useRef } from 'react';
import { custom, useFormApi, useFormState } from '@carboledger/ui';
import { FACILITY_KINDS, parseFacilityValue } from '@/config/formOptions';
import { TerminalField } from '@/components/common';
import { useGetTerminalMaterialIdsQuery } from '@/lib/api/lookupsApi';
import { getTranslation } from '@/lib/utils/i18n';
import { useTranslations } from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';

const TERMINAL_CLEARS = Object.freeze(['materialId', 'shipmentQuantityUnit']);

/**
 * Props:
 *   sites       the session's sites, which carry their terminals
 *   disabled    locked while only the quantity may change
 *   forOrder    Update shipment: the order fixes the material, the terminal is required and must carry it
 */
export const ShipmentTerminalField = ({ sites, disabled, forOrder = false }) => {
  const t = useTranslations();
  const { values } = useFormState();
  const { validateField } = useFormApi();

  const facility = parseFacilityValue(values.facilityId);
  const siteId = facility.kind === FACILITY_KINDS.site ? facility.id : null;
  const terminalId = values.terminalId || null;
  const materialId = values.materialId ? String(values.materialId) : null;
  const checksMaterial = forOrder && !disabled && Boolean(siteId && terminalId && materialId);

  // The materials Material settings assigned to the picked terminal.
  const { data: terminalMaterialIds, isFetching } = useGetTerminalMaterialIdsQuery(
    { siteId, terminalId },
    { skip: !checksMaterial },
  );

  // Rules are read when they run, so the check sees whatever list has loaded by then.
  const loaded = useRef({ terminalId: null, ids: null });
  loaded.current = isFetching
    ? { terminalId: null, ids: null }
    : { terminalId, ids: terminalMaterialIds ?? null };

  const rules = useMemo(
    () =>
      forOrder
        ? [
            custom((value) => {
              const { terminalId: checkedId, ids } = loaded.current;
              if (!value || !materialId || String(checkedId) !== String(value) || !ids) {
                return true;
              }
              return ids.map(String).includes(materialId);
            }, getTranslation('SHIPMENT_TERMINAL_MISSING_ORDER_MATERIAL')),
          ]
        : undefined,
    [forOrder, materialId],
  );

  // Shows the mismatch as soon as the terminal's list arrives, not only on Save.
  useEffect(() => {
    if (checksMaterial && !isFetching && terminalMaterialIds) {
      validateField('terminalId', terminalId, values);
    }
    // `values` is only the snapshot the rule reads; re-running on every keystroke would add nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checksMaterial, isFetching, terminalMaterialIds, terminalId, validateField]);

  if (facility.kind === FACILITY_KINDS.backToBack) return null;

  return (
    <TerminalField
      sites={sites}
      label={forOrder ? t('FIELD_TERMINAL') : t('FORM_TERMINAL_OPTIONAL')}
      // The order fixes material and unit, so picking its terminal must not wipe them.
      clears={forOrder ? EMPTY_LIST : TERMINAL_CLEARS}
      required={forOrder}
      rules={rules}
      disabled={disabled}
    />
  );
};
